import { TableCellTypes, TypographyColors } from '@carboledger/ui';
import {
  CellText,
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeViewBtnCell,
} from '@/components/common';
import { useTranslations } from '@/hooks';
import { ALLOCATION_STATUS_TONE } from '@/config/statusTones';
import { buildAllocationStatusVariants } from '@/config/statuses';
import { buildRowUnitOptions, unitTypeLabel } from './outgoingShipmentUnits';

// Outgoing shipment details ▸ Allocation details tab. Mirrors the orders detail tab's column set.

// TODO: promote alongside orders' orderAllocationColumns once one slice may edit the other.

// Below which "Days left" turns red — matches the allocations list's own URGENT_DAYS.
const URGENT_DAYS = 7;

const renderUnitType = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));
const renderCertifiedQuantity = makeUnitScopedCell('certifiedQuantity');

const DaysLeftCell = ({ value }) => {
  const t = useTranslations();
  if (value == null) return <CellText color={TypographyColors.secondary}>-</CellText>;
  const color = value < URGENT_DAYS ? TypographyColors.error : TypographyColors.primary;
  return <CellText color={color}>{t('ALLOCATIONS_DAYS_LEFT', { days: value })}</CellText>;
};
const renderDaysLeft = (ctx) => <DaysLeftCell value={ctx.value} />;

const hasDeclaration = (row) => row.associatedDeclarationNo !== '-';

// TODO: sorting is served by the backend; this route takes no order parameter yet.
const textColumn = (id, header, width = 200) => ({
  id,
  header,
  type: TableCellTypes.text,
  width,
  sortable: true,
  pinnable: true,
});

export const buildShipmentAllocationColumns = (t, { unitHeaderSelect, onViewDeclaration }) => [
  textColumn('transactionId', t('ALLOCATIONS_COL_TRANSACTION_ID'), 180),
  {
    id: 'status',
    header: t('FIELD_STATUS'),
    type: TableCellTypes.statusDot,
    width: 200,
    sortable: true,
    pinnable: true,
    variantMap: buildAllocationStatusVariants(t, ALLOCATION_STATUS_TONE),
  },
  textColumn('sendTo', t('ALLOCATIONS_COL_SEND_TO'), 192),
  textColumn('shippingTo', t('ALLOCATIONS_COL_SHIPPING_TO'), 192),
  textColumn('transactionDate', t('ORDERS_DETAIL_COL_ALLOCATED_DATE'), 170),
  {
    id: 'unitType',
    header: t('FIELD_UNIT_TYPE', { unit: unitTypeLabel(t, unitHeaderSelect.value) }),
    type: TableCellTypes.custom,
    width: 252,
    sortable: true,
    pinnable: true,
    cell: renderUnitType,
    headerSelect: unitHeaderSelect,
  },
  {
    id: 'certifiedQuantity',
    header: t('ALLOCATIONS_COL_CERTIFIED_QUANTITY'),
    type: TableCellTypes.custom,
    width: 210,
    sortable: true,
    pinnable: true,
    cell: renderCertifiedQuantity,
  },
  textColumn('shippingToSites', t('FIELD_SHIPPING_TO'), 192),
  textColumn('massBalancePeriod', t('ALLOCATIONS_COL_MASS_BALANCE_PERIOD'), 300),
  textColumn('reference', t('ALLOCATIONS_COL_REFERENCE'), 200),
  textColumn('associatedDeclarationNo', t('ALLOCATIONS_COL_ASSOCIATED_DECLARATION_NO'), 250),
  {
    id: 'declarationLink',
    header: t('ORDERS_DETAIL_COL_DECLARATIONS'),
    type: TableCellTypes.custom,
    width: 160,
    sortable: true,
    pinnable: true,
    cell: makeViewBtnCell(onViewDeclaration, hasDeclaration),
  },
  textColumn('transactedBy', t('ALLOCATIONS_COL_TRANSACTED_BY'), 260),
  textColumn('countryOfOrigin', t('FIELD_COUNTRY_OF_ORIGIN')),
  textColumn('rawMaterialType', t('FIELD_RAW_MATERIAL_TYPE'), 220),
  textColumn('ghgEmissions', t('FIELD_GHG_EMISSIONS'), 210),
  textColumn('ghgEmissionSavings', t('ALLOCATIONS_COL_GHG_EMISSION_SAVINGS'), 240),
  textColumn('feedstockCategory', t('FIELD_FEEDSTOCK_CATEGORY'), 210),
  textColumn('allocatedQuota', t('ALLOCATIONS_COL_ALLOCATED_QUOTA'), 210),
  textColumn('dateOfDispatch', t('FIELD_DATE_OF_DISPATCH'), 190),
  {
    id: 'daysLeft',
    header: t('FIELD_DAYS_LEFT'),
    type: TableCellTypes.custom,
    width: 170,
    sortable: true,
    pinnable: true,
    cell: renderDaysLeft,
  },
];
